import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, waitForProperty } from 'ember-concurrency';

import {
  isAllResourcesDayView,
  isDayView,
  isMachineView,
  isRoomView,
  isStaffView,
  isWeekView,
} from 'my-phorest/utils/calendar';
import { ActionsHistory } from 'my-phorest/utils/actions-history';
import { CalendarEvent } from 'my-phorest/utils/calendar-events';
import { hour, minute, parse } from 'my-phorest/utils/local-time-helpers';

import { DEFAULT_VIEW } from 'my-phorest/utils/calendar';

export default class FullCalendarService extends Service {
  @service localSettings;

  @tracked calendarApi;

  @tracked view;
  @tracked viewSingleResource;
  @tracked departmentId;

  @tracked _highlightedAppointments = [];
  @tracked _selectedAppointments = [];
  @tracked _selectedBreak;
  @tracked selectedResource;
  @tracked filteredStaffMembers = [];

  @tracked actionsHistory = new ActionsHistory();
  @tracked hideAppointmentIcons = false;
  @tracked limitTimeSlotsRange = true;
  @tracked isLoading = false;
  @tracked isReloadingResourcesSilently = false;

  onRegisterListeners = [];
  viewDatesInfo;

  addOnRegisterEventListener(func) {
    if (typeof func === 'function') {
      this.onRegisterListeners.push(func);
    }
  }

  clearActionsHistory() {
    this.actionsHistory.clear();
  }

  clearSelected() {
    this._highlightedAppointments = [];
    this._selectedAppointments = [];
    this._selectedBreak = null;
  }

  clearHighlightedRow() {
    let highlightedTimeRow = document.querySelector(
      `.fc-timegrid-slot.highlight`
    );

    if (highlightedTimeRow) {
      document
        .querySelector('.fc-timegrid-slot.highlight')
        .classList.remove('highlight');

      document
        .querySelector('.fc-timegrid-slots td.fc-timegrid-slot.highlight')
        ?.classList.remove('highlight');
    }
  }

  getAllAppointmentsForGroupBooking(groupBookingId) {
    if (!this.calendarApi) {
      console.error(
        'Trying to access the calendar API while it is not available yet.' +
          ' Please consider using `waitForCalendarApiTask` to ensure it has been registered first.'
      );
      return [];
    }

    return this.calendarApi
      .getEvents()
      .filter(
        (appointment) =>
          appointment.extendedProps.groupBookingId === groupBookingId
      )
      .map((e) => CalendarEvent.fromFCEvent(e));
  }

  refetchResourcesSilently() {
    if (typeof this.calendarApi?.refetchResources === 'function') {
      this.isReloadingResourcesSilently = true;
      this.calendarApi.refetchResources();
    }
  }

  changeToDefaultView() {
    this.calendarApi?.changeView('resourceTimeGridDay');
  }

  registerApi(calendar) {
    this.calendarApi = calendar;

    this.onRegisterListeners.forEach((func) => {
      func();
    });
    this.onRegisterListeners = [];
  }

  unregisterApi() {
    this.calendarApi = null;
    this.onRegisterListeners = [];
  }

  reset() {
    this.viewSingleResource = null;
    this.selectedResource = null;
    this.filteredStaffMembers = [];
    this.departmentId = null;
    this.clearSelected();
    this.clearActionsHistory();
    this.view = DEFAULT_VIEW;
    this.changeToDefaultView();
  }

  /**
   * Wait for the calendar API to be fully registered.
   * The task finishes as soon as the API is available.
   */
  @task
  *waitForCalendarApiTask() {
    yield waitForProperty(this, 'calendarApi', (c) => !!c);
  }

  get firstSelectedAppointment() {
    // XXX guaranteed to be the service that the user selected
    return this.hasSelectedAppointments ? this._selectedAppointments[0] : null;
  }

  get selectedAppointmentsByUserSelection() {
    // XXX guaranteed that the service the user clicked is first in list
    return this._selectedAppointments;
  }

  get selectedAppointments() {
    return [...this._selectedAppointments];
  }

  get highlightedAppointments() {
    return [...this._highlightedAppointments];
  }

  get selectedBreak() {
    return this._selectedBreak;
  }

  set selectedAppointments(selectedAppointments) {
    this.clearSelected();
    this._selectedAppointments = selectedAppointments;

    if (selectedAppointments.length) {
      this.selectedResource = selectedAppointments[0].resource;
    }
  }

  set highlightedAppointments(highlightedAppointments) {
    this._highlightedAppointments = highlightedAppointments;
  }

  set selectedBreak(selectedBreak) {
    this.clearSelected();
    this._selectedBreak = selectedBreak;

    this.selectedResource = selectedBreak.resource;
  }

  get hasSelectedAppointments() {
    return this._selectedAppointments?.length > 0;
  }

  get hasSelectedBreak() {
    return !!this._selectedBreak;
  }

  get hasSelectedResource() {
    return !!this.selectedResource;
  }

  get isAllResourcesDayView() {
    return isAllResourcesDayView(this.view);
  }

  get isDayView() {
    return isDayView(this.view);
  }

  get isRoomView() {
    return isRoomView(this.view);
  }

  get isStaffView() {
    return isStaffView(this.view);
  }

  get isMachineView() {
    return isMachineView(this.view);
  }

  get isWeekView() {
    return isWeekView(this.view);
  }

  get selectedAppointmentIds() {
    return this._selectedAppointments?.map((a) => a.id);
  }

  get isMultipleSelection() {
    return this.selectedAppointmentIds.length > 1;
  }

  get isDepartmentView() {
    return !!this.departmentId && !this.isWeekView;
  }

  get isCalendarFilteredByStaff() {
    return this.filteredStaffMembers.length > 0 && !this.isWeekView;
  }

  get appointmentsWithDifferentClientsSelected() {
    let clientId = this.firstSelectedAppointment?.clientId;
    return this.selectedAppointments.some((a) => a.clientId !== clientId);
  }

  get slotDurationMins() {
    let hours = hour(parse(this.calendarApi.getOption('slotDuration')));
    return (
      hours * 60 + minute(parse(this.calendarApi.getOption('slotDuration')))
    );
  }
}
